/**
 * „Plattform → Systemmail" (ADR 0014).
 *
 * Der Absender fuer Passwort-Reset und die Bestaetigung einer neuen
 * E-Mail-Adresse. Die Seite gehoert **keinem Mandanten** — sie ist nur fuer
 * Plattform-Admins sichtbar, und die API prueft das noch einmal
 * (`requirePlatformAdmin`). Die Sichtbarkeit hier ist Bequemlichkeit, nicht
 * Schutz.
 *
 * **Hier wird kein Schluessel mehr eingetippt** (ADR 0017): die Seite waehlt
 * eine `mail_provider`-Verbindung aus dem Vault und traegt daneben nur noch
 * Absenderadresse und -name ein. Wer den Zugang aendern will, tut das unter
 * „Plattform → Verbindungen" — an genau einer Stelle, fuer alle Verwendungen.
 *
 * `source` sagt, ob die geltende Einstellung schon aus der Datenbank kommt
 * oder noch aus `SYSTEM_MAIL_*` in `/etc/tickethub/env`. Ohne diesen Hinweis
 * waere „hier steht was" nicht von „das kommt woanders her" zu unterscheiden.
 */
import type { Connection, SystemMailConfigPublic } from '@tickethub/shared';
import { Send } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { platformApi } from '@/lib/tickets';

type Form = {
  connectionId: string;
  fromAddress: string;
  fromName: string;
};

const EMPTY: Form = { connectionId: '', fromAddress: '', fromName: '' };

function formOf(settings: SystemMailConfigPublic): Form {
  return {
    connectionId: settings.connection_id ?? '',
    fromAddress: settings.from_address,
    fromName: settings.from_name,
  };
}

export function PlatformMailPage() {
  const { t } = useTranslation();
  const { user } = useSession();

  const [settings, setSettings] = useState<SystemMailConfigPublic | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [loaded, list] = await Promise.all([
        platformApi.systemMail(),
        platformApi.connections(),
      ]);
      setSettings(loaded);
      setForm(formOf(loaded));
      setConnections(list.items.filter((entry) => entry.type === 'mail_provider'));
    } catch (error) {
      toastError(error, t);
    }
  }, [t]);

  useEffect(() => {
    if (!user?.is_platform_admin) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void load();
  }, [load, user?.is_platform_admin]);

  async function save() {
    setBusy(true);
    try {
      const saved = await platformApi.saveSystemMail({
        connection_id: form.connectionId === '' ? null : form.connectionId,
        from_address: form.fromAddress.trim().toLowerCase(),
        from_name: form.fromName.trim(),
      });
      setSettings(saved);
      setForm(formOf(saved));
      toast.success(t('platform.saved'));
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  async function sendTest() {
    setBusy(true);
    try {
      const result = await platformApi.testSystemMail();
      toast.success(t('platform.test_ok', { email: result.to, provider: result.provider }));
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  if (!user?.is_platform_admin) {
    return (
      <Alert>
        <AlertDescription>{t('platform.forbidden')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h1 className="text-xl font-semibold">{t('platform.system_mail_title')}</h1>
        <p className="text-sm text-muted-foreground">{t('platform.system_mail_intro')}</p>
        {settings?.source === 'env' ? (
          <Alert>
            <AlertDescription>{t('platform.source_env')}</AlertDescription>
          </Alert>
        ) : null}
        {connections.length === 0 ? (
          <Alert>
            <AlertDescription>{t('platform.no_connections')}</AlertDescription>
          </Alert>
        ) : null}
      </section>

      <section className="grid gap-3 rounded-lg border p-4 sm:grid-cols-2">
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="system-mail-connection">{t('mail.connection')}</Label>
          <Select
            id="system-mail-connection"
            value={form.connectionId}
            onChange={(event) => setForm({ ...form, connectionId: event.target.value })}
          >
            <option value="">{t('platform.connection_env')}</option>
            {connections.map((connection) => (
              <option key={connection.id} value={connection.id}>
                {connection.name}
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground">{t('platform.connection_hint')}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="system-mail-from">{t('mail.from_address')}</Label>
          <Input
            id="system-mail-from"
            type="email"
            value={form.fromAddress}
            onChange={(event) => setForm({ ...form, fromAddress: event.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="system-mail-from-name">{t('mail.from_name')}</Label>
          <Input
            id="system-mail-from-name"
            value={form.fromName}
            onChange={(event) => setForm({ ...form, fromName: event.target.value })}
          />
        </div>
      </section>

      <div className="flex flex-wrap gap-2">
        <Button disabled={busy} onClick={() => void save()}>
          {t('platform.save')}
        </Button>
        <Button variant="outline" disabled={busy} onClick={() => void sendTest()}>
          <Send aria-hidden="true" />
          {t('platform.test')}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">{t('platform.test_hint')}</p>
    </div>
  );
}
